<template>
  <div class="rationale-component-container">
    <div
      v-if="locked"
      class="locked"
    >
      <div class="locked__overlay" />
      <div class="locked__content">
        <div class="title">
          <span class="lock-icon">
            <icon
              name="lock"
              variant="white"
            />
          </span>
          <span class="text">Explanation Locked</span>
        </div>
        <div class="info">
          Access explanations when you {{ unlockActionText }}.
        </div>
        <div
          class="action"
          @click="showUpsell"
        >
          {{ unlockButtonText }}
        </div>
      </div>
    </div>
    <div class="title">
      Explanation
    </div>
    <div
      class="content"
      v-html="flashcard.rationale"
    />
    <div
      v-if="imagesEnabled && images.length > 0"
      class="images"
    >
      <img
        v-for="image in images"
        :key="image.url"
        :src="image.url"
      >
    </div>
    <div
      v-if="isHighlightQuestion"
      class="answer-legend"
      @click="showHighlightAnswerLegend"
    >
      View Answer Legend
    </div>
    <flashcard-share-popover
      v-if="sharingEnabled"
      :category="category"
      :context="'rationale'"
      :flashcard="flashcard"
    />
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';
import FlashcardSharePopover from '@components/flashcards/flashcard-share-popover';

import TrialMixin from '@/mixins/trial';

export default {
  name: 'Rationale',
  components: { Icon, FlashcardSharePopover },
  mixins: [TrialMixin],
  props: {
    category: {
      type: Object,
    },
    flashcard: {
      type: Object,
      default: () => ({}),
    },
    locked: {
      type: Boolean,
      default: false,
    },
    sharingEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    images() {
      return (this.flashcard.images || []).sort((a, b) => a.number - b.number);
    },
    imagesEnabled() {
      const pv = this.$store.getters['productVars/get']['flashcards_disable_image_ids'];
      const disabled = pv && pv.value === 'true';
      return !disabled;
    },
    isHighlightQuestion() {
      return this.flashcard.type === 'Highlight';
    },
    unlockActionText() {
      if (this.trialEnabled) {
        return 'start your trial';
      } else {
        return 'subscribe';
      }
    },
    unlockButtonText() {
      if (this.trialEnabled) {
        return `Try free for ${this.trialDurationText}`.toUpperCase();
      } else {
        return 'Subscribe Now';
      }
    },
  },
  methods: {
    showHighlightAnswerLegend() {
      this.$modal.show('highlight-legend');
    },

    showUpsell() {
      this.$analytics.events.clickedUpsell(this.$store, 'Locked Content', this.type);
      const modalParams = {
        show: true,
        resource: {
          type: 'category',
          id: this.category?.id,
        },
        source: {
          type: 'Locked Rationale',
          value: this.flashcard.id,
        },
      };
      this.$store.dispatch('ui/toggleUpsellModal', modalParams);
    },
  },
};
</script>

<style lang='scss' scoped>
   .answer-legend {
     text-align: center;
     color: #555555;
     text-decoration: underline;
     cursor: pointer;
     font-size: 14px;
     line-height: 24px;
   }
  .rationale-component-container {
    background: #f2f4f7;
    padding: rem-calc(12);
    margin: rem-calc(20) 0;
    border-radius: 8px;
    font-size: rem-calc(16);
    position: relative;

    .locked {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      &__overlay {
        position: absolute;
        top: 0;
        bottom: 0;
        left: -50%;
        right: -50%;
        background: linear-gradient(to bottom, rgba(0,0,0, 0.85), rgba(0,0,0,0.99));
        background-color: rgba(0,0,0,.85);
        // This isn't super well supported (no FF, no IE) but should still cover most of our users
        backdrop-filter: blur(4px);
        opacity: 0.96;
      }
      &__content {
        position: absolute;
        bottom: 0;
        top: 0;
        left: 0;
        right: 0;
        margin: auto;
        background-color: white;
        width: rem-calc(335);
        height: rem-calc(196);
        border-radius: 8px;
        display: flex;
        flex-flow: column nowrap;
        justify-content: center;
        align-items: center;
        .title {
          font-size: rem-calc(26);
          font-weight: $bold;
          color: #000000;
          margin-bottom: rem-calc(16);
          .lock-icon {
            width: 10px;
            padding: 7px;
            font-size: 10px;
            border-radius: 20px;
            background-color: #1c4a59;
            position: relative;
            top: -4px;
            margin-right: 5px;
            img {
              display: inline;
              width: 10px;
              width: 13px;
              position: relative;
              top: 2px;
            }
          }
        }
        .info {
          color: #53606e;
          font-size: rem-calc(14);
          margin-bottom: rem-calc(33);
        }
        .action {
          width: rem-calc(287);
          height: rem-calc(48);
          background-color: #7bd56e;
          border-radius: 2px;
          box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.25);
          display: flex;
          justify-content: center;
          align-items: center;
          color: white;
          font-size: rem-calc(14);
          font-weight: 600;
          cursor: pointer;
        }
      }
    }

    .title {
      color: #000000;
      font-size: rem-calc(20);
      font-weight: $bold;
    }

    .content {
      margin-top: rem-calc(16);
      line-height: rem-calc(24);

      :deep(img) {
        display: block;
        margin: auto;
        max-width: 100%;
        width: auto;
      }
    }

    .images {
      background: $white-1;
      border-radius: rem-calc(4);
      box-sizing: border-box;
      box-shadow: 0 1px 4px 0 rgba(0,0,0,.12);
      margin-top: rem-calc(36);
      padding: rem-calc(48);
      text-align: center;

      img {
        margin: 0 auto rem-calc(16);
        height: auto;
        max-width: rem-calc(480);
      }
    }

    .asset-associations-list-container {
      margin-top: rem-calc(32);
    }

  }
</style>
